import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';

// V2 디자인 시스템
const designSystem = {
  colors: {
    primary: '#2467E7',
    primaryDark: '#1B4DB8',
    primaryLight: '#E8F2FF',
    secondary: '#6C5CE7',
    accent: '#00D4FF',
    success: '#00B894',
    warning: '#FDCB6E',
    danger: '#E17055',
    bgPrimary: '#FAFBFC',
    bgSecondary: '#FFFFFF',
    textPrimary: '#1A202C',
    textSecondary: '#718096',
    textMuted: '#A0AEC0',
    border: '#E2E8F0'
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.12)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.15)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.2)'
  }
};

interface SiteGroup {
  id: number;
  site_name: string;
}

interface SiteUrl {
  id: number;
  url: string;
  label: string;
  display_order: number;
  site_group_id: number;
  group_name: string;
  created_at: string;
  updated_at: string;
}

const SiteUrlsManager: React.FC = () => {
  const { isAdmin, user, isLoading } = useAuth();
  const [urls, setUrls] = useState<SiteUrl[]>([]);
  const [groups, setGroups] = useState<SiteGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 필터 상태
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');

  // 편집 상태
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<SiteUrl>>({});

  // CSV 업로드 상태
  const [uploadStatus, setUploadStatus] = useState<string>('');

  // 선택 상태 (대량 삭제용)
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // URL 추가 모달 상태
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    site_group_id: '',
    new_group_name: '',
    url: '',
    label: ''
  });

  useEffect(() => {
    if (!isLoading && isAdmin && user) {
      fetchGroups();
      fetchUrls();
    }
  }, [isAdmin, user, isLoading, selectedGroupId, searchQuery]);

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('google_auth_token');
      const response = await fetch(getApiUrl('/api/admin/site-groups?limit=1000'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGroups(data.data || []);
      }
    } catch (err) {
      console.error('그룹 조회 실패:', err);
    }
  };

  const fetchUrls = async () => {
    try {
      setLoading(true);
      setError(null);
      setSelectedIds([]); // 필터 변경 시 선택 초기화

      const token = localStorage.getItem('google_auth_token');
      if (!token) {
        throw new Error('인증 토큰이 없습니다.');
      }

      const params = new URLSearchParams();
      if (selectedGroupId) params.append('groupId', selectedGroupId);
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', '1000');

      const response = await fetch(getApiUrl(`/api/admin/site-groups/urls/all?${params.toString()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API 오류: ${response.status}`);
      }

      const data = await response.json();
      setUrls(data.data || []);
    } catch (err) {
      console.error('URL 조회 실패:', err);
      setError(err instanceof Error ? err.message : '알 수 없는 오류');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (url: SiteUrl) => {
    setEditingId(url.id);
    setEditForm({
      url: url.url,
      label: url.label,
      display_order: url.display_order, // API 호환성을 위해 유지 (화면에는 미표시)
      site_group_id: url.site_group_id
    });
  };

  const handleSave = async (id: number) => {
    try {
      const token = localStorage.getItem('google_auth_token');
      const response = await fetch(getApiUrl(`/api/admin/site-groups/urls/${id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        throw new Error('저장 실패');
      }

      setEditingId(null);
      fetchUrls();
      alert('저장되었습니다.');
    } catch (err) {
      console.error('URL 저장 실패:', err);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('google_auth_token');
      const response = await fetch(getApiUrl(`/api/admin/site-groups/urls/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('삭제 실패');
      }

      fetchUrls();
      alert('삭제되었습니다.');
    } catch (err) {
      console.error('URL 삭제 실패:', err);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      alert('삭제할 항목을 선택해주세요.');
      return;
    }

    if (!window.confirm(`선택한 ${selectedIds.length}개의 URL을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('google_auth_token');
      const response = await fetch(getApiUrl('/api/admin/site-groups/urls/bulk-delete'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: selectedIds })
      });

      if (!response.ok) {
        throw new Error('삭제 실패');
      }

      const result = await response.json();
      setSelectedIds([]);
      fetchUrls();
      alert(result.message);
    } catch (err) {
      console.error('대량 삭제 실패:', err);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(urls.map(url => url.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    }
  };

  const handleAddUrl = async () => {
    if ((!addForm.site_group_id && !addForm.new_group_name) || !addForm.url) {
      alert('그룹(기존 선택 또는 새 그룹명)과 URL은 필수입니다.');
      return;
    }

    try {
      const token = localStorage.getItem('google_auth_token');
      let groupId = addForm.site_group_id;

      // 새 그룹명이 입력된 경우 그룹 먼저 생성
      if (addForm.new_group_name) {
        const createGroupResponse = await fetch(getApiUrl('/api/admin/site-groups'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            site_name: addForm.new_group_name,
            description: null
          })
        });

        if (!createGroupResponse.ok) {
          throw new Error('그룹 생성 실패');
        }

        const newGroup = await createGroupResponse.json();
        groupId = newGroup.data.id.toString();

        // 그룹 목록 새로고침
        await fetchGroups();
      }

      // URL 추가
      const response = await fetch(getApiUrl(`/api/admin/site-groups/${groupId}/urls`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url: addForm.url,
          label: addForm.label || null,
          display_order: 999 // 자동으로 맨 뒤에 추가
        })
      });

      if (!response.ok) {
        throw new Error('URL 추가 실패');
      }

      setShowAddModal(false);
      setAddForm({ site_group_id: '', new_group_name: '', url: '', label: '' });
      fetchUrls();
      alert('URL이 추가되었습니다.');
    } catch (err) {
      console.error('URL 추가 실패:', err);
      alert('추가 중 오류가 발생했습니다.');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadStatus('파일 읽는 중...');

      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());

      // CSV 파싱 (헤더: group_name, url, label, display_order)
      const urls = lines.slice(1).map(line => {
        const [group_name, url, label, display_order] = line.split(',').map(s => s.trim());
        return {
          group_name: group_name || '미분류',
          url: url || '',
          label: label || '',
          display_order: parseInt(display_order) || 0
        };
      }).filter(u => u.url);

      if (urls.length === 0) {
        alert('유효한 URL 데이터가 없습니다.');
        setUploadStatus('');
        return;
      }

      setUploadStatus(`${urls.length}개 URL 업로드 중...`);

      const token = localStorage.getItem('google_auth_token');
      const response = await fetch(getApiUrl('/api/admin/site-groups/urls/bulk'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ urls })
      });

      if (!response.ok) {
        throw new Error('업로드 실패');
      }

      const result = await response.json();
      setUploadStatus(result.message);
      fetchUrls();
      fetchGroups();

      setTimeout(() => setUploadStatus(''), 5000);
    } catch (err) {
      console.error('CSV 업로드 실패:', err);
      alert('업로드 중 오류가 발생했습니다.');
      setUploadStatus('');
    }

    // 파일 입력 초기화
    event.target.value = '';
  };

  const downloadTemplate = () => {
    const csv = 'group_name,url,label,display_order\n정부기관,https://example.com,예시사이트,1\n';
    // UTF-8 BOM 추가 (엑셀 한글 깨짐 방지)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'site_urls_template.csv';
    link.click();
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: designSystem.colors.danger }}>관리자 권한이 필요합니다.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* 헤더 */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', color: designSystem.colors.textPrimary }}>
          사이트 URL 관리
        </h1>
        <p style={{ color: designSystem.colors.textSecondary }}>
          전체 URL을 테이블로 관리하고, CSV 파일로 대량 추가/수정할 수 있습니다.
        </p>
      </div>

      {/* 필터 및 액션 바 */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* 그룹 필터 */}
        <select
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          style={{
            padding: '0.5rem 1rem',
            border: `1px solid ${designSystem.colors.border}`,
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            minWidth: '200px'
          }}
        >
          <option value="">전체 그룹</option>
          {groups.map(group => (
            <option key={group.id} value={group.id}>{group.site_name}</option>
          ))}
        </select>

        {/* 검색 */}
        <input
          type="text"
          placeholder="URL, 라벨, 그룹명 검색..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: '0.5rem 1rem',
            border: `1px solid ${designSystem.colors.border}`,
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            flex: '1',
            minWidth: '300px'
          }}
        />

        {/* URL 추가 버튼 */}
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: designSystem.colors.success,
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          ➕ URL 추가
        </button>

        {/* CSV 템플릿 다운로드 */}
        <button
          onClick={downloadTemplate}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: designSystem.colors.bgSecondary,
            color: designSystem.colors.primary,
            border: `1px solid ${designSystem.colors.primary}`,
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          📥 템플릿 다운로드
        </button>

        {/* CSV 파일 업로드 */}
        <label style={{
          padding: '0.5rem 1rem',
          backgroundColor: designSystem.colors.primary,
          color: 'white',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          cursor: 'pointer',
          fontWeight: '500',
          display: 'inline-block'
        }}>
          📤 CSV 업로드
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
        </label>

        {uploadStatus && (
          <span style={{ color: designSystem.colors.success, fontSize: '0.875rem' }}>
            {uploadStatus}
          </span>
        )}

        {/* 선택 항목 삭제 버튼 */}
        {selectedIds.length > 0 && (
          <button
            onClick={handleBulkDelete}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: designSystem.colors.danger,
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            🗑️ 선택 항목 삭제 ({selectedIds.length}개)
          </button>
        )}
      </div>

      {/* 로딩/에러 상태 */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem', color: designSystem.colors.textSecondary }}>
          데이터 로딩 중...
        </div>
      )}

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#FEE',
          color: designSystem.colors.danger,
          borderRadius: '0.5rem',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {/* URL 테이블 */}
      {!loading && !error && (
        <div style={{
          backgroundColor: designSystem.colors.bgSecondary,
          borderRadius: '0.75rem',
          boxShadow: designSystem.shadows.md,
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: designSystem.colors.bgPrimary }}>
                  <th style={{ ...tableHeaderStyle, width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={urls.length > 0 && selectedIds.length === urls.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th style={tableHeaderStyle}>ID</th>
                  <th style={tableHeaderStyle}>그룹</th>
                  <th style={{ ...tableHeaderStyle, minWidth: '300px' }}>URL</th>
                  <th style={tableHeaderStyle}>라벨</th>
                  <th style={tableHeaderStyle}>액션</th>
                </tr>
              </thead>
              <tbody>
                {urls.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: designSystem.colors.textMuted }}>
                      URL이 없습니다.
                    </td>
                  </tr>
                ) : (
                  urls.map(url => (
                    <tr key={url.id} style={{
                      borderBottom: `1px solid ${designSystem.colors.border}`,
                      backgroundColor: editingId === url.id ? designSystem.colors.primaryLight : 'transparent'
                    }}>
                      <td style={tableCellStyle}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(url.id)}
                          onChange={(e) => handleSelectOne(url.id, e.target.checked)}
                          disabled={editingId === url.id}
                          style={{ cursor: editingId === url.id ? 'not-allowed' : 'pointer' }}
                        />
                      </td>
                      <td style={tableCellStyle}>{url.id}</td>
                      <td style={tableCellStyle}>
                        {editingId === url.id ? (
                          <select
                            value={editForm.site_group_id}
                            onChange={(e) => setEditForm({ ...editForm, site_group_id: parseInt(e.target.value) })}
                            style={inputStyle}
                          >
                            {groups.map(group => (
                              <option key={group.id} value={group.id}>{group.site_name}</option>
                            ))}
                          </select>
                        ) : (
                          <span style={{ fontSize: '0.875rem', color: designSystem.colors.secondary, fontWeight: '500' }}>
                            {url.group_name}
                          </span>
                        )}
                      </td>
                      <td style={tableCellStyle}>
                        {editingId === url.id ? (
                          <input
                            type="text"
                            value={editForm.url}
                            onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
                            style={{ ...inputStyle, width: '100%' }}
                          />
                        ) : (
                          <a href={url.url} target="_blank" rel="noopener noreferrer"
                            style={{ color: designSystem.colors.primary, textDecoration: 'none', fontSize: '0.875rem' }}>
                            {url.url}
                          </a>
                        )}
                      </td>
                      <td style={tableCellStyle}>
                        {editingId === url.id ? (
                          <input
                            type="text"
                            value={editForm.label}
                            onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                            style={inputStyle}
                          />
                        ) : (
                          <span style={{ fontSize: '0.875rem' }}>{url.label || '-'}</span>
                        )}
                      </td>
                      <td style={tableCellStyle}>
                        {editingId === url.id ? (
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={() => handleSave(url.id)} style={saveButtonStyle}>
                              ✓
                            </button>
                            <button onClick={() => setEditingId(null)} style={cancelButtonStyle}>
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={() => handleEdit(url)} style={editButtonStyle}>
                              수정
                            </button>
                            <button onClick={() => handleDelete(url.id)} style={deleteButtonStyle}>
                              삭제
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* 결과 카운트 */}
          <div style={{
            padding: '1rem',
            borderTop: `1px solid ${designSystem.colors.border}`,
            color: designSystem.colors.textSecondary,
            fontSize: '0.875rem'
          }}>
            총 {urls.length}개의 URL
          </div>
        </div>
      )}

      {/* CSV 포맷 안내 */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        backgroundColor: designSystem.colors.primaryLight,
        borderRadius: '0.5rem',
        fontSize: '0.875rem',
        color: designSystem.colors.textSecondary
      }}>
        <strong>CSV 파일 형식:</strong>
        <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
          <li>첫 줄: group_name,url,label,display_order</li>
          <li>그룹이 없으면 자동 생성됩니다.</li>
          <li>같은 URL이 이미 있으면 업데이트됩니다.</li>
          <li>예시: 정부기관,https://example.com,예시사이트,1</li>
        </ul>
      </div>

      {/* URL 추가 모달 */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: designSystem.colors.bgSecondary,
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: designSystem.shadows.xl
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              marginBottom: '1.5rem',
              color: designSystem.colors.textPrimary
            }}>
              URL 추가
            </h2>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: designSystem.colors.textSecondary
              }}>
                기존 그룹 선택
              </label>
              <select
                value={addForm.site_group_id}
                onChange={(e) => setAddForm({ ...addForm, site_group_id: e.target.value, new_group_name: '' })}
                disabled={!!addForm.new_group_name}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${designSystem.colors.border}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  opacity: addForm.new_group_name ? 0.5 : 1,
                  cursor: addForm.new_group_name ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="">그룹을 선택하세요</option>
                {groups.map(group => (
                  <option key={group.id} value={group.id}>{group.site_name}</option>
                ))}
              </select>
            </div>

            <div style={{
              textAlign: 'center',
              margin: '1rem 0',
              color: designSystem.colors.textMuted,
              fontSize: '0.875rem'
            }}>
              또는
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: designSystem.colors.textSecondary
              }}>
                새 그룹 만들기
              </label>
              <input
                type="text"
                value={addForm.new_group_name}
                onChange={(e) => setAddForm({ ...addForm, new_group_name: e.target.value, site_group_id: '' })}
                disabled={!!addForm.site_group_id}
                placeholder="새 그룹명을 입력하세요"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${designSystem.colors.border}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  opacity: addForm.site_group_id ? 0.5 : 1,
                  cursor: addForm.site_group_id ? 'not-allowed' : 'text'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: designSystem.colors.textSecondary
              }}>
                URL <span style={{ color: designSystem.colors.danger }}>*</span>
              </label>
              <input
                type="url"
                value={addForm.url}
                onChange={(e) => setAddForm({ ...addForm, url: e.target.value })}
                placeholder="https://example.com"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${designSystem.colors.border}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: designSystem.colors.textSecondary
              }}>
                라벨
              </label>
              <input
                type="text"
                value={addForm.label}
                onChange={(e) => setAddForm({ ...addForm, label: e.target.value })}
                placeholder="사이트명 또는 설명"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${designSystem.colors.border}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setAddForm({ site_group_id: '', new_group_name: '', url: '', label: '' });
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: designSystem.colors.bgPrimary,
                  color: designSystem.colors.textSecondary,
                  border: `1px solid ${designSystem.colors.border}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                취소
              </button>
              <button
                onClick={handleAddUrl}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: designSystem.colors.success,
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                추가
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 스타일 상수
const tableHeaderStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  textAlign: 'left',
  fontSize: '0.75rem',
  fontWeight: '600',
  color: designSystem.colors.textSecondary,
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const tableCellStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  fontSize: '0.875rem',
  color: designSystem.colors.textPrimary
};

const inputStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  border: `1px solid ${designSystem.colors.border}`,
  borderRadius: '0.25rem',
  fontSize: '0.875rem'
};

const editButtonStyle: React.CSSProperties = {
  padding: '0.25rem 0.75rem',
  backgroundColor: designSystem.colors.primaryLight,
  color: designSystem.colors.primary,
  border: 'none',
  borderRadius: '0.25rem',
  fontSize: '0.75rem',
  cursor: 'pointer',
  fontWeight: '500'
};

const deleteButtonStyle: React.CSSProperties = {
  padding: '0.25rem 0.75rem',
  backgroundColor: '#FEE',
  color: designSystem.colors.danger,
  border: 'none',
  borderRadius: '0.25rem',
  fontSize: '0.75rem',
  cursor: 'pointer',
  fontWeight: '500'
};

const saveButtonStyle: React.CSSProperties = {
  padding: '0.25rem 0.75rem',
  backgroundColor: designSystem.colors.success,
  color: 'white',
  border: 'none',
  borderRadius: '0.25rem',
  fontSize: '0.75rem',
  cursor: 'pointer',
  fontWeight: '500'
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '0.25rem 0.75rem',
  backgroundColor: designSystem.colors.textMuted,
  color: 'white',
  border: 'none',
  borderRadius: '0.25rem',
  fontSize: '0.75rem',
  cursor: 'pointer',
  fontWeight: '500'
};

export default SiteUrlsManager;
